import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import SubMenuItem from './SubMenuItem';

/**
 * Menu Item Component
 * Represents a single menu item with optional children (sub-menu)
 * Supports expand/collapse functionality for items with children
 * Memoized to prevent unnecessary re-renders
 */
const MenuItem = React.memo(
  ({
    item,
    isActive,
    activeItemId,
    isExpanded,
    onToggleExpand,
    onItemClick,
    onSubItemClick,
  }) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    // Active green style only applies to leaf items (no children)
    const showActiveStyle = isActive && !hasChildren;

    // Handle main item click
    const handleClick = useCallback(
      (e) => {
        if (hasChildren && !item.to && !item.action) {
          // If item has children and no direct action, just toggle expand
          e.preventDefault();
          onToggleExpand(item.id);
        } else {
          // Otherwise, handle the click through onItemClick
          onItemClick(item);
        }
      },
      [hasChildren, item, onToggleExpand, onItemClick]
    );

    // Determine if we should use Link or button
    const ItemWrapper = item.to ? Link : 'button';
    const itemProps = item.to
      ? { to: item.to }
      : { type: 'button', onClick: handleClick };

    return (
      <li className="relative">
        <ItemWrapper
          {...itemProps}
          onClick={item.to ? () => onItemClick(item) : handleClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            height: '2.938rem',
            background: showActiveStyle ? 'rgba(57, 1, 255, 0.08)' : '#fff',
            fontFamily: 'Maven Pro, sans-serif',
            fontWeight: 500,
            fontSize: '0.96rem',
            lineHeight: '1.188rem',
            color: showActiveStyle ? '#3901ff' : '#2b2b2b',
            padding: '0.938rem 2rem',
            textDecoration: 'none',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {Icon && (
              <Icon
                style={{
                  color: showActiveStyle ? '#3901ff' : '#2b2b2b',
                  marginRight: '0.625rem',
                  verticalAlign: 'middle',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}
              />
            )}
            <span className="truncate">{item.title}</span>
          </div>

          {hasChildren && (
            <span className="flex-shrink-0 ml-2">
              {isExpanded ? (
                <ChevronDown style={{ width: '1rem', height: '1rem', color: '#2b2b2b' }} />
              ) : (
                <ChevronRight style={{ width: '1rem', height: '1rem', color: '#2b2b2b' }} />
              )}
            </span>
          )}
        </ItemWrapper>

        {/* Sub-menu items */}
        {hasChildren && isExpanded && (
          <ul
            style={{
              background: '#fff',
              listStyleType: 'none',
              padding: 0,
              margin: 0
            }}
            role="menu"
          >
            {item.children.map((child) => (
              <SubMenuItem
                key={child.id}
                item={child}
                isActive={activeItemId === child.id}
                onClick={onSubItemClick}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }
);

MenuItem.displayName = 'MenuItem';

MenuItem.propTypes = {
  /** Menu item configuration */
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    to: PropTypes.string,
    icon: PropTypes.elementType,
    action: PropTypes.string,
    children: PropTypes.array,
  }).isRequired,

  /** Whether this item is currently active */
  isActive: PropTypes.bool,

  /** Whether this item's children are expanded */
  isExpanded: PropTypes.bool,

  /** Callback to toggle expand state */
  onToggleExpand: PropTypes.func.isRequired,

  /** Callback when item is clicked */
  onItemClick: PropTypes.func.isRequired,

  /** Callback when sub-item is clicked */
  onSubItemClick: PropTypes.func.isRequired,
};

MenuItem.defaultProps = {
  isActive: false,
  isExpanded: false,
};

export default MenuItem;
